import classnames from 'classnames';
import { ForwardedRef, forwardRef, PropsWithChildren, Ref, useMemo } from 'react';

import { radioGroupContext, RadioGroupContext } from './RadioButton/RadioButton.types';
import styles from './RadioGroup.module.scss';
import { RadioGroupProps } from './RadioGroup.types';

const RadioGroupInner = <T extends string, TClearable extends boolean = false>(
  {
    value,
    children,
    onValueChange,
    small,
    vertical,
    clearable,
    disabled,
    error,
    className,
  }: PropsWithChildren<RadioGroupProps<T, TClearable>>,
  ref: ForwardedRef<HTMLDivElement>
) => {
  const radioGroup = useMemo(
    (): RadioGroupContext => ({
      selectedValue: value,
      onSelectValue: clickedValue => {
        // @ts-expect-error We can call onValueChange with undefined because we know that clearable is true
        if (clearable && clickedValue === value) onValueChange?.(undefined);
        else onValueChange?.(clickedValue as T);
      },
      small,
      vertical,
      disabled,
      error,
    }),
    [clearable, disabled, error, onValueChange, small, value, vertical]
  );

  return (
    <radioGroupContext.Provider value={radioGroup}>
      <div
        className={classnames(styles.radioGroup, className, vertical && styles.vertical)}
        ref={ref}
      >
        {children}
      </div>
    </radioGroupContext.Provider>
  );
};

export const RadioGroup = forwardRef(RadioGroupInner) as <
  T extends string,
  TClearable extends boolean = false,
>(
  props: PropsWithChildren<RadioGroupProps<T, TClearable>> & { ref?: Ref<HTMLDivElement> }
) => ReturnType<typeof RadioGroupInner>;
